import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from './services/authService';
import { resolveSignInEntrypointAction } from './auth/signInEntrypoint';
import { isRequireFreshLogin } from './auth/passwordChangedSession';
import { consumePostLoginReturnTo } from './brandSupportDeepLink';

/**
 * Canonical MA login entrypoint.
 *
 * Always hands off to branded Auth0 Universal Login when unauthenticated.
 * Never render an unbranded intermediate "Sign In to KrossKast" button page
 * after logout (or on a fresh visit).
 *
 * After password-reset completion, requireFreshLogin forces Auth0 prompt=login
 * and blocks navigate-app. The gate is peeked here (not consumed) so a failed
 * callback can retry with prompt=login; consumption happens on successful
 * /callback only.
 */
const SignIn = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const startedRef = useRef(false);
  // The pending deep link belongs to exactly one login attempt; re-entering
  // /signin (Back, reload, later visit) starts a new attempt without it.
  const returnToRef = useRef(undefined);

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      // Prevent double loginWithRedirect from effect re-entry (redirect loop).
      if (startedRef.current) return;
      startedRef.current = true;

      try {
        // Stale flag from older logout flow — clear so it cannot block Auth0.
        sessionStorage.removeItem('justLoggedOut');

        // Peek only — do not consume. Successful /callback clears the gate.
        const requireFreshLogin = isRequireFreshLogin();
        if (returnToRef.current === undefined) {
          returnToRef.current = consumePostLoginReturnTo();
        }
        const action = resolveSignInEntrypointAction({
          isLoggedIn: authService.isLoggedIn(),
          requireFreshLogin,
        });

        if (action.type === 'navigate-app') {
          navigate(action.path, { replace: true });
          return;
        }

        const result = await authService.loginWithRedirect({
          ...(action.prompt ? { prompt: action.prompt } : {}),
          returnTo: returnToRef.current,
        });
        if (!cancelled && result && result.success === false) {
          startedRef.current = false;
          setError(result.error || 'Unable to start sign-in');
        }
      } catch (err) {
        if (!cancelled) {
          startedRef.current = false;
          setError(err?.message || 'Unable to start sign-in');
        }
      }
    };

    start();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleRetry = () => {
    setError(null);
    startedRef.current = false;
    const requireFreshLogin = isRequireFreshLogin();
    startedRef.current = true;
    authService.loginWithRedirect({
      ...(requireFreshLogin ? { prompt: 'login' } : {}),
      returnTo: returnToRef.current,
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: '12px',
        textAlign: 'center',
        padding: '20px',
      }}
    >
      {error ? (
        <>
          <div>{error}</div>
          <button type="button" onClick={handleRetry}>
            Retry Sign In
          </button>
        </>
      ) : (
        <div>Redirecting to sign in…</div>
      )}
    </div>
  );
};

export default SignIn;
